import { useCallback, useState } from "react"
import { useAppDispatch } from "../hooks"
import * as API from "./api"
import { setResults, setSelectedIndex } from "./searchSlice"
import { SearchResult, type SearchType } from './types'
import { featureSetToGraphicsArray } from "../parcels/hooks"

import { useAddressToLocations } from "../map/locator/hooks"
import * as LocatorLayer from "../map/layers/LocatorLayer"
import * as SearchResultsLayer from "../map/layers/SearchResultsLayer"
import * as SelectionLayer from "../map/layers/SelectionLayer"
import * as ComplexParcelDetailsLayer from "../map/layers/ComplexParcelDetailsLayer"
import { useNavigate } from "react-router-dom"
import { zoomTo } from "../map"


const searchByOwnerName = async (ownerName: string): Promise<__esri.Graphic[]> => {
  const results = await API.searchByOwnerName(ownerName) as __esri.FeatureSet
  if (!results || !results.features.length) return []
  return featureSetToGraphicsArray(results)
}

const searchByPin = async (pin: string): Promise<__esri.Graphic[]> => {
  const results = await API.searchByPin(pin) as __esri.FeatureSet
  if (!results || !results.features.length) return []
  return featureSetToGraphicsArray(results)
}

const searchByTms = async (tms: string): Promise<__esri.Graphic[]> => {
  const results = await API.searchByTms(tms) as __esri.FeatureSet
  if (!results || !results.features.length) return []
  return featureSetToGraphicsArray(results)
}


export const useSearch = () => {
  const dispatch = useAppDispatch()
  const addressSearch = useAddressToLocations()
  
  const [searchType, setSearchType] = useState<SearchType>("Address")
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [query, setQuery] = useState<string>("")

  const changeSearchType = useCallback((mode: SearchType) => {
    if (!isSearching) {
      setSearchType(mode)
    }
  }, [setSearchType, isSearching])

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isSearching) {
      setQuery(e.target.value)
    }
  }, [setQuery, isSearching])

  const search = useCallback(() => {
    if (searchType === null || isSearching) return
    if (query.trim() === "") return
    setIsSearching(true)
    SearchResultsLayer.clearGraphics()
    LocatorLayer.clearGraphics()
    const runSearch = async () => {
      let results: SearchResult[]
      let graphics: __esri.Graphic[] = []
      switch (searchType) {
        case "PIN":
          graphics = await searchByPin(query)
          results = SearchResultsLayer.addParcels(searchType, graphics)
          break
        case "TMS":
          graphics = await searchByTms(query)
          results = SearchResultsLayer.addParcels(searchType, graphics)
          break
        case "Owner":
          graphics = await searchByOwnerName(query)
          results = SearchResultsLayer.addParcels(searchType, graphics)
          break
        case "Address":
          const candidates = await addressSearch(query)
          results = LocatorLayer.addCandidates(candidates)
          break
        default:
          setIsSearching(false)
          return
      }
      dispatch(setResults(results))
      setIsSearching(false)
    }
    runSearch()
  }, [searchType, setIsSearching, isSearching, query, dispatch, addressSearch])


  const onKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      search()
    }
  }, [search])

  return [ 
    {
      mode: searchType,
      isSearching,
      query,
    },
    {
      onChange,
      changeMode: changeSearchType,
      onKeyUp,
      search,
    }]
}

export const useHighlightSearchResult = (type: SearchType, handle: number) => {
  return useCallback((active: boolean) => {
    if (type === "Address" && active) {
      LocatorLayer.highlightGraphic(handle)
    } else if (type === "Address" && !active) {
      LocatorLayer.unhighlightGraphic(handle)
    } else if (active) {
      SearchResultsLayer.highlightGraphic(handle)
    } else {
      SearchResultsLayer.unhighlightGraphic(handle)
    }
  }, [type, handle])
}

export const useSelectSearchResult = (result: SearchResult, index: number) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  return useCallback(() => {
    if (result.type !== "Address") {
      SearchResultsLayer.unhighlightGraphic(result.handle)
      ComplexParcelDetailsLayer.clearGraphics()
      SelectionLayer.selectSearchResult(result)
      dispatch(setSelectedIndex(index))
      navigate(`/pin/${result.PIN}`)
    } else {
      zoomTo(result.location, 17)
    }
  }, [result, navigate, index, dispatch])
}